/* PerfumeGoal theme — vanilla JS, no dependencies. */
(() => {
  const $ = (sel, ctx = document) => ctx.querySelector(sel);
  const $$ = (sel, ctx = document) => Array.from(ctx.querySelectorAll(sel));
  const { routes, moneyFormat, cartType } = window.theme || {};
  const CART_SECTION = 'cart-drawer';
  const designMode = Boolean(window.Shopify && window.Shopify.designMode);

  /* ---------- Money ---------- */
  function formatMoney(cents, format = moneyFormat || '${{amount}}') {
    const value = Number(cents) / 100;
    const fmt = (decimals, thousands = ',', decimal = '.') => {
      const [int, frac] = value.toFixed(decimals).split('.');
      return int.replace(/\B(?=(\d{3})+(?!\d))/g, thousands) + (frac ? decimal + frac : '');
    };
    return format.replace(/\{\{\s*(\w+)\s*\}\}/, (_, key) => {
      switch (key) {
        case 'amount_no_decimals': return fmt(0);
        case 'amount_with_comma_separator': return fmt(2, '.', ',');
        case 'amount_no_decimals_with_comma_separator': return fmt(0, '.', ',');
        case 'amount_with_apostrophe_separator': return fmt(2, "'", '.');
        case 'amount_with_space_separator': return fmt(2, ' ', ',');
        default: return fmt(2);
      }
    });
  }

  /* ---------- Drawers ---------- */
  let lastFocus = null;

  function openDrawer(drawer) {
    if (!drawer) return;
    lastFocus = document.activeElement;
    drawer.classList.add('is-open');
    drawer.setAttribute('aria-hidden', 'false');
    document.documentElement.classList.add('no-scroll');
    const focusable = $('input:not([type=hidden]), button, a[href]', $('.drawer__panel', drawer));
    if (focusable) setTimeout(() => focusable.focus({ preventScroll: true }), 50);
  }

  function closeDrawer(drawer) {
    if (!drawer) return;
    drawer.classList.remove('is-open');
    drawer.setAttribute('aria-hidden', 'true');
    if (!$('.drawer.is-open')) document.documentElement.classList.remove('no-scroll');
    if (lastFocus) lastFocus.focus({ preventScroll: true });
  }

  document.addEventListener('click', (event) => {
    const opener = event.target.closest('[data-open]');
    if (opener) {
      const target = document.getElementById(opener.dataset.open);
      if (target) {
        event.preventDefault();
        openDrawer(target);
      }
      return;
    }
    const closer = event.target.closest('[data-drawer-close]');
    if (closer) closeDrawer(closer.closest('.drawer'));
  });

  document.addEventListener('keydown', (event) => {
    if (event.key === 'Escape') $$('.drawer.is-open').forEach(closeDrawer);
  });

  /* ---------- Cart ---------- */
  async function renderCart(sections) {
    const html = sections && sections[CART_SECTION];
    if (html) {
      const doc = new DOMParser().parseFromString(html, 'text/html');
      const next = $('[data-cart-drawer-content]', doc);
      const current = $('[data-cart-drawer-content]');
      if (next && current) current.innerHTML = next.innerHTML;
    }
    const cart = await fetch(`${routes.cart_url}.js`, { headers: { Accept: 'application/json' } }).then((r) => r.json());
    $$('[data-cart-count]').forEach((el) => {
      el.textContent = cart.item_count;
      el.hidden = cart.item_count === 0;
    });
    return cart;
  }

  async function changeLine(line, quantity) {
    const drawer = $('#CartDrawer');
    drawer.classList.add('is-loading');
    try {
      const res = await fetch(`${routes.cart_change_url}.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ line: Number(line), quantity: Math.max(0, Number(quantity)), sections: CART_SECTION })
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.description || data.message);
      await renderCart(data.sections);
    } catch (error) {
      console.error(error);
      window.location.href = routes.cart_url;
    } finally {
      drawer.classList.remove('is-loading');
    }
  }

  document.addEventListener('click', (event) => {
    const button = event.target.closest('[data-line-change]');
    if (button) changeLine(button.dataset.lineChange, button.dataset.qty);
  });

  /* ---------- Quantity steppers ---------- */
  document.addEventListener('click', (event) => {
    const step = event.target.closest('[data-qty-step]');
    if (!step) return;
    const input = $('input', step.closest('.qty'));
    const min = Number(input.min || 0);
    input.value = Math.max(min, Number(input.value || 0) + Number(step.dataset.qtyStep));
    input.dispatchEvent(new Event('change', { bubbles: true }));
  });

  // Cart page: save quantity changes automatically.
  let updateTimer;
  document.addEventListener('change', (event) => {
    if (!event.target.matches('[data-auto-update]')) return;
    clearTimeout(updateTimer);
    updateTimer = setTimeout(() => {
      const form = event.target.form;
      form.requestSubmit($('[data-update-button]', form));
    }, 500);
  });

  /* ---------- Product forms ---------- */
  function initProductForms(root) {
    $$('form[data-type="product-form"]', root).forEach((form) => {
      if (form.dataset.bound) return;
      form.dataset.bound = 'true';

      form.addEventListener('submit', async (event) => {
        if (cartType === 'page') return;
        event.preventDefault();
        const button = $('[data-add-button]', form);
        const error = $('[data-form-error]', form);
        button.disabled = true;
        button.classList.add('is-loading');
        if (error) error.hidden = true;

        const body = new FormData(form);
        body.append('sections', CART_SECTION);
        body.append('sections_url', window.location.pathname);

        try {
          const res = await fetch(`${routes.cart_add_url}.js`, {
            method: 'POST',
            headers: { Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
            body
          });
          const data = await res.json();
          if (!res.ok || data.status) throw new Error(data.description || data.message || 'Unable to add to bag.');
          await renderCart(data.sections);
          openDrawer($('#CartDrawer'));
        } catch (err) {
          if (error) {
            error.textContent = err.message;
            error.hidden = false;
          }
        } finally {
          button.classList.remove('is-loading');
          button.disabled = button.dataset.soldOut === 'true';
        }
      });
    });
  }

  /* ---------- Variant picker ---------- */
  function initVariantPickers(root) {
    $$('[data-variant-picker]', root).forEach((picker) => {
      const section = picker.closest('[data-product]');
      const json = $('[data-variants-json]', section);
      if (!json || picker.dataset.bound) return;
      picker.dataset.bound = 'true';
      const variants = JSON.parse(json.textContent);

      picker.addEventListener('change', () => {
        const groups = $$('fieldset', picker);
        const selected = groups.map((group) => {
          const checked = $('input:checked', group);
          $('[data-option-value]', group).textContent = checked ? checked.value : '';
          return checked ? checked.value : null;
        });
        const variant = variants.find((v) => v.options.every((opt, i) => opt === selected[i]));

        const button = $('[data-add-button]', section);
        const label = $('[data-add-label]', section);
        if (!variant) {
          button.disabled = true;
          button.dataset.soldOut = 'true';
          label.textContent = 'Unavailable';
          return;
        }

        $('[data-variant-id]', section).value = variant.id;
        $('[data-price]', section).textContent = formatMoney(variant.price);
        const compare = $('[data-compare-price]', section);
        if (compare) {
          const onSale = variant.compare_at_price && variant.compare_at_price > variant.price;
          compare.hidden = !onSale;
          if (onSale) compare.textContent = formatMoney(variant.compare_at_price);
        }

        button.disabled = !variant.available;
        button.dataset.soldOut = String(!variant.available);
        label.textContent = variant.available ? button.dataset.addText : button.dataset.soldText;

        const url = new URL(window.location.href);
        url.searchParams.set('variant', variant.id);
        window.history.replaceState({}, '', url);

        if (variant.featured_media) {
          const slide = $(`[data-media-id="${variant.featured_media.id}"]`, section);
          const gallery = $('[data-gallery]', section);
          if (slide && gallery) {
            if (window.matchMedia('(max-width: 989px)').matches) {
              gallery.scrollTo({ left: slide.offsetLeft, behavior: 'smooth' });
            } else if (slide !== gallery.firstElementChild) {
              gallery.prepend(slide);
            }
          }
        }
      });
    });
  }

  /* ---------- Gallery dots (mobile) ---------- */
  function initGalleries(root) {
    $$('[data-gallery]', root).forEach((gallery) => {
      const dots = $$('.gallery__dot', gallery.parentElement);
      if (!dots.length) return;
      gallery.addEventListener('scroll', () => {
        const index = Math.round(gallery.scrollLeft / gallery.clientWidth);
        dots.forEach((dot, i) => dot.classList.toggle('is-active', i === index));
      }, { passive: true });
    });
  }

  /* ---------- Collection sorting ---------- */
  document.addEventListener('change', (event) => {
    if (!event.target.matches('[data-sort]')) return;
    const url = new URL(window.location.href);
    url.searchParams.set('sort_by', event.target.value);
    url.searchParams.delete('page');
    window.location.href = url.toString();
  });

  /* ---------- Confirm-before-submit ---------- */
  document.addEventListener('submit', (event) => {
    const message = event.target.dataset.confirm;
    if (message && !window.confirm(message)) event.preventDefault();
  });

  /* ---------- Header ---------- */
  function initHeader() {
    const header = $('[data-header]');
    if (!header) return;
    const update = () => header.classList.toggle('is-scrolled', window.scrollY > 40);
    update();
    window.addEventListener('scroll', update, { passive: true });

    // Publish the real header height (it grows with the logo) for overlays and sticky offsets.
    const setHeight = () => document.documentElement.style.setProperty('--header-h', `${header.offsetHeight}px`);
    setHeight();
    if ('ResizeObserver' in window) new ResizeObserver(setHeight).observe(header);
  }

  function initAnnouncements(root) {
    $$('[data-announcement]', root).forEach((bar) => {
      const messages = $$('.announcement__msg', bar);
      if (messages.length < 2 || bar.dataset.bound) return;
      bar.dataset.bound = 'true';
      let index = 0;
      setInterval(() => {
        messages[index].classList.remove('is-active');
        index = (index + 1) % messages.length;
        messages[index].classList.add('is-active');
      }, 4500);
    });
  }

  /* ---------- Reveal on scroll ---------- */
  const revealObserver = 'IntersectionObserver' in window
    ? new IntersectionObserver((entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;
          entry.target.classList.add('is-visible');
          revealObserver.unobserve(entry.target);
        });
      }, { rootMargin: '0px 0px -8% 0px', threshold: 0.08 })
    : null;

  function initReveal(root) {
    $$('.reveal:not(.is-visible)', root).forEach((el) => {
      if (designMode || !revealObserver || document.body.classList.contains('no-animate')) {
        el.classList.add('is-visible');
      } else {
        revealObserver.observe(el);
      }
    });
  }

  /* ---------- AI Perfume Finder ---------- */
  const escapeHtml = (value) =>
    String(value ?? '').replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' })[c]);

  const chipList = (items) => items.map((item) => `<li>${escapeHtml(item)}</li>`).join('');

  function aiLoading() {
    return `
      <div class="ai-loading">
        <span class="ai-loading__orb" aria-hidden="true"></span>
        <p class="eyebrow">Composing your scent profile</p>
        <p class="muted">Our AI perfumer is reading your notes…</p>
      </div>`;
  }

  function aiMessage(text, kind = 'error') {
    return `<div class="ai-message ai-message--${kind}"><p>${escapeHtml(text)}</p></div>`;
  }

  function aiResults(data, byHandle) {
    if (data.notice) return aiMessage(data.notice, 'notice');
    const p = data.profile;
    const meta = [
      ['Family', p.scent_family],
      ['Intensity', p.intensity],
      ['Wear', p.time_of_day],
      ['Lasts', p.longevity],
      ['Choose', p.concentration]
    ].filter(([, value]) => value);

    const facets = [
      ['Flavours', p.flavours],
      ['Smells like', p.smells_like],
      ['Mood & intent', p.mood],
      ['Occasions', p.occasions],
      ['Seasons', p.seasons],
      ['Best avoided', p.avoid]
    ].filter(([, list]) => list && list.length);

    const tiers = [['Top', p.notes.top], ['Heart', p.notes.heart], ['Base', p.notes.base]].filter(([, list]) => list.length);

    const matches = data.matches
      .map((m) => ({ ...m, product: byHandle.get(m.handle) }))
      .filter((m) => m.product);

    const cards = matches.map((m, i) => {
      const prod = m.product;
      const image = prod.image
        ? `<img src="${escapeHtml(prod.image)}" alt="${escapeHtml(prod.title)}" loading="lazy" width="600" height="750">`
        : '<span class="ai-match__noimg" aria-hidden="true"></span>';
      return `
        <article class="ai-match" style="--d:${i * 90}ms">
          <a href="${escapeHtml(prod.url)}" class="ai-match__media">${image}
            <span class="ai-match__score" style="--pct:${m.match}"><b>${m.match}%</b><small>match</small></span>
          </a>
          <div class="ai-match__info">
            <h3 class="ai-match__title"><a href="${escapeHtml(prod.url)}">${escapeHtml(prod.title)}</a></h3>
            <p class="ai-match__reason">${escapeHtml(m.reason)}</p>
            <p class="ai-match__price">${prod.priceVaries ? 'From ' : ''}${escapeHtml(prod.price)}${prod.available ? '' : ' · Sold out'}</p>
            <a href="${escapeHtml(prod.url)}" class="link-arrow">Discover <svg class="icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.1" aria-hidden="true"><path d="M4 12h16M14 6l6 6-6 6"/></svg></a>
          </div>
        </article>`;
    }).join('');

    return `
      <div class="ai-profile">
        <div class="ai-profile__intro">
          <p class="eyebrow">Your scent profile</p>
          <h2 class="h2">${escapeHtml(p.headline)}</h2>
          <p class="ai-profile__summary">${escapeHtml(p.summary)}</p>
          ${meta.length ? `<dl class="ai-meta">${meta.map(([k, v]) => `<div><dt>${k}</dt><dd>${escapeHtml(v)}</dd></div>`).join('')}</dl>` : ''}
        </div>
        ${tiers.length ? `
        <div class="ai-pyramid" aria-label="Recommended notes">
          ${tiers.map(([label, list]) => `<div class="ai-pyramid__tier"><span class="eyebrow">${label} notes</span><p>${list.map(escapeHtml).join(', ')}</p></div>`).join('')}
        </div>` : ''}
      </div>
      ${facets.length ? `<div class="ai-facets">${facets.map(([label, list]) => `<div class="ai-facet"><p class="eyebrow">${label}</p><ul>${chipList(list)}</ul></div>`).join('')}</div>` : ''}
      ${p.tips.length ? `<div class="ai-tips"><p class="eyebrow">Wearing tips</p><ul>${chipList(p.tips)}</ul></div>` : ''}
      <div class="ai-matches">
        <div class="ai-matches__head">
          <p class="eyebrow">Your matches at PerfumeGoal</p>
          <h3 class="h3">${matches.length ? 'Perfumes chosen for you' : 'No close match in the collection yet'}</h3>
          ${matches.length ? '' : `<p class="muted">Use the notes above to explore, or try describing your scent another way.</p>`}
        </div>
        ${matches.length ? `<div class="ai-matches__grid">${cards}</div>` : ''}
      </div>`;
  }

  function initAiFinders(root) {
    $$('[data-ai-finder]', root).forEach((section) => {
      if (section.dataset.bound) return;
      section.dataset.bound = 'true';

      const form = $('[data-ai-form]', section);
      const textarea = $('textarea', form);
      const submit = $('[data-ai-submit]', section);
      const results = $('[data-ai-results]', section);
      const endpoint = section.dataset.endpoint;
      let catalog = [];
      try {
        catalog = JSON.parse($('[data-ai-catalog]', section).textContent);
      } catch (err) {
        console.error('AI finder: product data could not be read', err);
      }
      const byHandle = new Map(catalog.map((p) => [p.handle, p]));
      let controller;

      $$('[data-ai-chip]', section).forEach((chip) =>
        chip.addEventListener('click', () => {
          textarea.value = chip.textContent.trim();
          form.requestSubmit();
        })
      );

      textarea.addEventListener('keydown', (event) => {
        if (event.key === 'Enter' && !event.shiftKey) {
          event.preventDefault();
          form.requestSubmit();
        }
      });

      form.addEventListener('submit', async (event) => {
        event.preventDefault();
        const query = textarea.value.trim();
        if (query.length < 3) {
          textarea.focus();
          return;
        }
        results.hidden = false;
        if (!endpoint) {
          results.innerHTML = aiMessage('AI search is not connected yet. Please check back soon.');
          return;
        }

        if (controller) controller.abort();
        controller = new AbortController();
        submit.disabled = true;
        submit.classList.add('is-loading');
        results.innerHTML = aiLoading();
        results.scrollIntoView({ behavior: 'smooth', block: 'start' });

        try {
          const res = await fetch(endpoint, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            signal: controller.signal,
            body: JSON.stringify({
              query,
              preferences: { for: form.elements.for.value, occasion: form.elements.occasion.value },
              catalog: catalog.map(({ handle, title, type, tags, notes, description }) => ({ handle, title, type, tags, notes, description }))
            })
          });
          const data = await res.json().catch(() => ({}));
          if (!res.ok) throw new Error(data.error || 'Something went wrong. Please try again.');
          results.innerHTML = aiResults(data, byHandle);
          initReveal(results);
        } catch (err) {
          if (err.name === 'AbortError') return;
          results.innerHTML = aiMessage(
            err instanceof TypeError ? 'We could not reach the perfume advisor. Check your connection and try again.' : err.message
          );
        } finally {
          submit.disabled = false;
          submit.classList.remove('is-loading');
        }
      });
    });
  }

  /* ---------- Init ---------- */
  function init(root = document) {
    initAiFinders(root);
    initProductForms(root);
    initVariantPickers(root);
    initGalleries(root);
    initAnnouncements(root);
    initReveal(root);
  }

  initHeader();
  init();

  // Re-initialise sections when edited in the theme editor.
  document.addEventListener('shopify:section:load', (event) => {
    if (event.target.querySelector('[data-header]')) initHeader();
    init(event.target);
  });
  document.addEventListener('shopify:section:select', (event) => {
    const drawer = $('#CartDrawer', event.target);
    if (drawer) openDrawer(drawer);
  });
  document.addEventListener('shopify:section:deselect', (event) => {
    const drawer = $('#CartDrawer', event.target);
    if (drawer) closeDrawer(drawer);
  });
})();
